import {put, call, all} from 'redux-saga/effects';
import FlatFee from "../../entities/flatFee";
import * as Actions from "../actions/flatFeeActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {ensureOperationId} from '../../lib/pendingRows';
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type FlatFeeAction = {
    flatFeeId: string;
    flatFee: FlatFee;
    flatFees: FlatFee[];
    sentAddedOn?: number;
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneFlatFee(action: FlatFeeAction) {
	const session = tabSession.captureGeneration();
	try {
		const flatFee: FlatFee = yield call(api.findOne, 'flatFee', action.flatFeeId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.FLATFEE_FETCH_ONE_SUCCESS, flatFeeId: action.flatFeeId, flatFee, sentAddedOn: action.sentAddedOn});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.FLATFEE_FETCH_ONE_FAILURE, flatFeeId: action.flatFeeId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {FlatFeeAction} action
 */
export function* saveFlatFee(action: FlatFeeAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ FLATFEE_SAVE saga', action.flatFee?.id);

		if (action.flatFee === undefined || action.flatFee === null) {
			console.error('saveFlatFee received a null');
			throw new Error('saveFlatFee received a null');
		}

		if (!(action.flatFee instanceof FlatFee)) {
			action.flatFee = new FlatFee(action.flatFee);
		}
		// OWA-1018: the reducer showed this save at dispatch, stamped after the version this tab holds
		// (OWA-1083), marked pending under this queue key
		const dispatchedAddedOn = action.flatFee.addedOn;

		try {
			// Save to IndexedDB
			ensureOperationId(action.flatFee);
			const addResult = yield call(idb.add, 'flatFee', action.flatFee);
			if (addResult.isErr()) {
				console.error(`⮂ saveFlatFee: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The save never reached the queue: its row returns to the latest accepted version
				yield put(Actions.revertFlatFeeSave(action.flatFee, {sentAddedOn: dispatchedAddedOn}));
				yield put({type: Actions.FLATFEE_SAVE_FAILURE, flatFee: action.flatFee, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copy replaces the row shown at dispatch
			// (the queue may have stamped it again)
			yield put({type: Actions.FLATFEE_SAVE, flatFee: action.flatFee, dispatchedAddedOn, queuedAt: performance.now()});

			// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
			// removes it when it saves; a draft typed somewhere else may be newer than this copy.
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue);
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: a save that never reached the queue returns its row to the latest accepted version
			if (!queueReported) yield put(Actions.revertFlatFeeSave(action.flatFee, {sentAddedOn: dispatchedAddedOn}));
			reportQueued(false);
			yield put({type: Actions.FLATFEE_SAVE_FAILURE, flatFee: action.flatFee, status: 500, err});

			console.error('Error saving to database', err);
		}
    } finally { reportQueued(false); }
}

/**
 * TODO: Update for saveMany
 * @param {FlatFeeAction} action
 * @param {FlatFee[]} action.flatFees
 */
export function* saveManyFlatFees(action: FlatFeeAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ FLATFEE_SAVE_MANY saga', action.flatFees.map(flatFee => flatFee.id).join(','));

        if (!Array.isArray(action.flatFees)) {
            console.error('saveManyFlatFees expected an array, received', action.flatFees);
            throw new Error('saveManyFlatFees expected an array');
        }
		// OWA-1018: the reducer showed these saves at dispatch (see saveFlatFee), under these queue keys
		const dispatchedAddedOns: number[] = action.flatFees.map(flatFee => flatFee?.addedOn);
		const revertAll = () => action.flatFees.map((flatFee, i) =>
			put(Actions.revertFlatFeeSave(flatFee, {sentAddedOn: dispatchedAddedOns[i]})));

		try {
			const toSave: FlatFee[] = [];

			// Save each item to IndexedDB
			for(let i = 0; i < action.flatFees.length; i++) {
				const flatFee = action.flatFees[i];
				flatFee.persisted = false;
				ensureOperationId(flatFee);
				toSave.push(flatFee);
			}

			const addResult = yield call(idb.add, 'flatFee', toSave);
			if (addResult.isErr()) {
				console.error(`⮂ saveManyFlatFees: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The saves never reached the queue: their rows return to the latest accepted versions
				yield all(revertAll());
				yield put({type: Actions.FLATFEE_SAVE_MANY_FAILURE, flatFees: action.flatFees, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copies replace the rows shown at dispatch
			yield put({type: Actions.FLATFEE_SAVE_MANY, flatFees: toSave, dispatchedAddedOns, queuedAt: performance.now()});
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue)
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: saves that never reached the queue return their rows to the latest accepted versions
			if (!queueReported) yield all(revertAll());
			reportQueued(false);
			yield put({type: Actions.FLATFEE_SAVE_MANY_FAILURE, flatFees: action.flatFees, status: 500, err});

			console.error(`Error saving ${action.flatFees.length} flatFees to database`, err);
		}
    } finally { reportQueued(false); }
}

/**
 * @param {FlatFeeAction} action
 */
export function* removeFlatFee(action: FlatFeeAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
	if (!userId || action.flatFee === undefined || action.flatFee === null || !(action.flatFee instanceof FlatFee)) {
			console.error('removeFlatFee expected type FlatFee, received', action.flatFee);
			reportQueued(false);
			throw new Error('removeFlatFee expected type FlatFee');
		}

	try {
		// Mark as deleted. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.flatFee.status = 'D';
		action.flatFee.addedOn = newHRTimestamp();
		action.flatFee.addedBy = userId;

		// Save to IndexedDB
		ensureOperationId(action.flatFee);
		const addResult = yield call(idb.add, 'flatFee', action.flatFee);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeFlatFee: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			yield put({type: Actions.FLATFEE_REMOVE_FAILURE, flatFee: action.flatFee, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.FLATFEE_REMOVE, flatFee: action.flatFee});
		session.assertCurrent();

        reportQueued(true);

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.FLATFEE_REMOVE_FAILURE, flatFee: action.flatFee, status: 500, err});

		console.error('Error deleting from database', err);
	}
    finally { reportQueued(false); }
}

/**
	* Restore the last version of this FlatFee.
	*
	* @param {FlatFeeAction} action
	*/
export function* restoreFlatFee(action: FlatFeeAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreFlatFee expected getCurrentUserId() to return a value');
        return;
    }

	if (action.flatFee === undefined || action.flatFee === null) {
        console.error('restoreFlatFee expected a FlatFee, received null');
        throw new Error('restoreFlatFee expected a FlatFee, received null');
    }
    else if (!(action.flatFee instanceof FlatFee)) action.flatFee = new FlatFee(action.flatFee); 

	try {
		// Mark as restored. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.flatFee.status = 'U';
		action.flatFee.addedOn = newHRTimestamp();
		action.flatFee.addedBy = userId;
		action.flatFee.restore = true;

		// Save to IndexedDB
		ensureOperationId(action.flatFee);
		const addResult = yield call(idb.add, 'flatFee', action.flatFee);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreFlatFee: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.FLATFEE_RESTORE_FAILURE, flatFee: action.flatFee, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.FLATFEE_RESTORE, flatFee: action.flatFee});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.FLATFEE_RESTORE_FAILURE, flatFee: action.flatFee, status: 500, err});

		console.error('Error restoring from database', err);
	}
}